/**
 * FhirConditionListWidget — Active conditions for subject dashboard (DRR-321)
 *
 * Shows conditions/diagnoses from FHIR Condition resources.
 * Widget version of the conditions display from FhirClinicalPanel.
 */

import React, { useMemo } from 'react';
import {
  Stack,
  Text,
  Badge,
  Group,
  Card,
  Skeleton,
  Alert,
  Box,
  ScrollArea,
} from '@mantine/core';
import { IconStethoscope, IconAlertCircle, IconCalendar } from '@tabler/icons-react';
import { useAdminListFhirConditionsQuery } from '@ferp/core/generated/graphql';
import type { WidgetProps } from '@ferp/core';

interface ParsedCondition {
  id: string;
  code: string;
  display: string;
  clinicalStatus: string;
  verificationStatus: string;
  onsetDate?: string;
  severity?: string;
  category?: string;
}

function parseCondition(resourceId: string, json: string): ParsedCondition | null {
  try {
    const f = JSON.parse(json) as Record<string, unknown>;

    const codeCC = f.code as { text?: string; coding?: Array<{ code?: string; display?: string }> } | undefined;
    const coding = codeCC?.coding?.[0];
    const display = codeCC?.text ?? coding?.display ?? 'Unknown condition';
    const code = coding?.code ?? '';

    const clinicalCC = f.clinicalStatus as { coding?: Array<{ code?: string }> } | undefined;
    const clinicalStatus = clinicalCC?.coding?.[0]?.code ?? 'unknown';

    const verifCC = f.verificationStatus as { coding?: Array<{ code?: string }> } | undefined;
    const verificationStatus = verifCC?.coding?.[0]?.code ?? 'unknown';

    const severityCC = f.severity as { text?: string; coding?: Array<{ display?: string }> } | undefined;
    const severity = severityCC?.text ?? severityCC?.coding?.[0]?.display;

    const categoryCC = f.category as Array<{ text?: string; coding?: Array<{ display?: string }> }> | undefined;
    const category = categoryCC?.[0]?.text ?? categoryCC?.[0]?.coding?.[0]?.display;

    let onsetDate: string | undefined;
    if (typeof f.onsetDateTime === 'string') {
      onsetDate = f.onsetDateTime;
    } else if (f.onsetPeriod && typeof (f.onsetPeriod as Record<string, unknown>).start === 'string') {
      onsetDate = (f.onsetPeriod as Record<string, unknown>).start as string;
    }

    return {
      id: resourceId,
      code,
      display,
      clinicalStatus,
      verificationStatus,
      onsetDate,
      severity,
      category,
    };
  } catch {
    return null;
  }
}

const STATUS_COLOR: Record<string, string> = {
  active: 'red',
  recurrence: 'orange',
  relapse: 'orange',
  inactive: 'gray',
  remission: 'green',
  resolved: 'blue',
};

const SEVERITY_COLOR: Record<string, string> = {
  severe: 'red',
  moderate: 'orange',
  mild: 'yellow',
};

export function FhirConditionListWidget({ tenantNum }: WidgetProps) {
  const { data, loading, error } = useAdminListFhirConditionsQuery({
    variables: { tenantNum: tenantNum ?? 0, limit: 30 },
    skip: !tenantNum,
    fetchPolicy: 'cache-and-network',
  });

  const conditions = useMemo(() => {
    const raw = data?.adminListFhirConditions ?? [];
    return raw
      .filter(r => r.isLatest !== false)
      .map(r => parseCondition(r.resourceId, r.fhirJson))
      .filter((c): c is ParsedCondition => c !== null);
  }, [data]);

  if (loading && conditions.length === 0) {
    return (
      <Stack gap="sm">
        <Skeleton height={50} />
        <Skeleton height={50} />
        <Skeleton height={50} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        Error loading conditions.
      </Alert>
    );
  }

  if (conditions.length === 0) {
    return (
      <Alert icon={<IconStethoscope size={16} />} color="blue" variant="light">
        No conditions documented for this subject.
      </Alert>
    );
  }

  const activeConditions = conditions.filter(c => c.clinicalStatus === 'active');
  const otherConditions = conditions.filter(c => c.clinicalStatus !== 'active');

  return (
    <Stack gap="sm">
      <Group gap="xs">
        <Text fw={600} size="sm">Conditions</Text>
        <Badge size="sm" color="red">{activeConditions.length} Active</Badge>
        {otherConditions.length > 0 && (
          <Badge size="sm" variant="light">{otherConditions.length} Resolved/Other</Badge>
        )}
      </Group>

      <ScrollArea.Autosize mah={250}>
        <Stack gap="xs">
          {activeConditions.map((condition) => (
            <Card key={condition.id} withBorder padding="xs" radius="sm">
              <Group justify="space-between" wrap="nowrap">
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text fw={500} size="sm" lineClamp={1}>{condition.display}</Text>
                  <Group gap="xs" mt={4}>
                    {condition.code && (
                      <Badge size="xs" variant="outline" color="gray">{condition.code}</Badge>
                    )}
                    {condition.severity && (
                      <Badge size="xs" color={SEVERITY_COLOR[condition.severity.toLowerCase()] ?? 'gray'}>
                        {condition.severity}
                      </Badge>
                    )}
                    {condition.onsetDate && (
                      <Group gap={2}>
                        <IconCalendar size={10} color="gray" />
                        <Text size="xs" c="dimmed">
                          {new Date(condition.onsetDate).toLocaleDateString()}
                        </Text>
                      </Group>
                    )}
                  </Group>
                </Box>
                <Badge size="xs" color={STATUS_COLOR[condition.clinicalStatus] ?? 'gray'}>
                  {condition.clinicalStatus}
                </Badge>
              </Group>
            </Card>
          ))}

          {otherConditions.length > 0 && (
            <Text size="xs" c="dimmed" ta="center">
              +{otherConditions.length} resolved/inactive condition{otherConditions.length !== 1 ? 's' : ''}
            </Text>
          )}
        </Stack>
      </ScrollArea.Autosize>
    </Stack>
  );
}
